import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DateInputPicker } from '@/components/ui/date-input-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { UserAvatar } from '@/components/ui/user-avatar'
import { cn } from '@/lib/utils'
import { parseCalendarDateValue, toDateOnlyString } from '@/utils/date'
import {
  ArrowsDownUpIcon,
  CheckIcon,
  EyeSlashIcon,
  FunnelIcon,
  PencilSimpleIcon,
  XIcon,
} from '@phosphor-icons/react'

import { isBlankAddressFilter } from '../column-filter-utils'
import { ColumnFilterValue } from '../types'
import { AddressFilterEditor } from './AddressFilterEditor'

interface ColumnMenuPopoverProps {
  columnId: string
  columnLabel: string
  fieldType?: string
  filterValue?: ColumnFilterValue
  onFilterChange?: (columnId: string, value: ColumnFilterValue | null) => void
  // Column actions
  canHide?: boolean
  canSort?: boolean
  canFilter?: boolean
  canEdit?: boolean // Whether this column's field definition can be edited
  canRename?: boolean // Whether this column's display label can be renamed inline
  customFieldDefinitionId?: string // ID of the custom field definition (for edit functionality)
  onHideColumn?: (columnId: string) => void
  onToggleSort?: (columnId: string) => void
  onEditField?: (customFieldDefinitionId: string) => void
  onRename?: (columnId: string, newLabel: string) => void
  // Trigger element
  children: ReactNode
  // Options for DROPDOWN and USER fields
  dropdownOptions?: Array<{ value: string; label: string }>
  userOptions?: Array<{ id: string; name: string; email?: string }>
  // Callback when popover open state changes
  onOpenChange?: (open: boolean) => void
  triggerClassName?: string
  triggerTitle?: string
}

export function ColumnMenuPopover({
  columnId,
  columnLabel,
  fieldType = 'STRING',
  filterValue,
  onFilterChange,
  canHide = true,
  canSort = true,
  canFilter = true,
  canEdit = false,
  canRename = false,
  customFieldDefinitionId,
  onHideColumn,
  onToggleSort,
  onEditField,
  onRename,
  children,
  dropdownOptions = [],
  userOptions = [],
  onOpenChange,
  triggerClassName,
  triggerTitle,
}: ColumnMenuPopoverProps) {
  const [open, setOpen] = useState(false)

  // Notify parent when open state changes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    onOpenChange?.(newOpen)
  }
  const [showFilter, setShowFilter] = useState(false)
  const [showRename, setShowRename] = useState(false)
  const [renameValue, setRenameValue] = useState(columnLabel)
  const [localValue, setLocalValue] = useState<ColumnFilterValue | null>(
    filterValue || null,
  )
  // Search term for DROPDOWN and USER filters - must be at top level to follow Rules of Hooks
  const [filterSearchTerm, setFilterSearchTerm] = useState('')

  // Sync local value with external filter value
  useEffect(() => {
    setLocalValue(filterValue || null)
    // Also reset showFilter when filter is cleared externally
    if (
      !filterValue ||
      filterValue.value === null ||
      filterValue.value === undefined ||
      (Array.isArray(filterValue.value) && filterValue.value.length === 0)
    ) {
      setShowFilter(false)
    }
  }, [filterValue])

  useEffect(() => {
    if (open && filterValue) {
      setShowFilter(true)
    }
    // Reset search term when popover closes
    if (!open) {
      setFilterSearchTerm('')
      setShowRename(false)
    }
  }, [open, filterValue])

  const handleApply = () => {
    if (
      localValue &&
      localValue.value !== null &&
      localValue.value !== '' &&
      !isBlankAddressFilter(localValue) &&
      (!Array.isArray(localValue.value) || localValue.value.length > 0)
    ) {
      onFilterChange(columnId, localValue)
    } else {
      onFilterChange(columnId, null)
    }
  }

  const handleClear = () => {
    setLocalValue(null)
    onFilterChange(columnId, null)
    setShowFilter(false)
  }

  const handleSort = () => {
    if (onToggleSort) {
      onToggleSort(columnId)
      handleOpenChange(false)
    }
  }

  const handleHide = () => {
    if (onHideColumn) {
      onHideColumn(columnId)
      handleOpenChange(false)
    }
  }

  const handleEdit = () => {
    if (onEditField && customFieldDefinitionId) {
      onEditField(customFieldDefinitionId)
      handleOpenChange(false)
    }
  }

  const openRenameView = () => {
    setRenameValue(columnLabel)
    setShowRename(true)
  }

  const handleRenameSave = () => {
    if (!onRename) return
    onRename(columnId, renameValue.trim())
    setShowRename(false)
    handleOpenChange(false)
  }

  const toggleFilterView = () => {
    setShowFilter(!showFilter)
  }

  const hasActiveFilter =
    filterValue &&
    filterValue.value !== null &&
    filterValue.value !== '' &&
    (!Array.isArray(filterValue.value) || filterValue.value.length > 0)

  const renderFilterContent = () => {
    switch (fieldType) {
      case 'NUMBER':
      case 'CURRENCY': {
        const numValue = localValue?.value as
          | { min?: number; max?: number }
          | undefined
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label
                  htmlFor="min-value"
                  className="text-xs text-muted-foreground"
                >
                  Min
                </Label>
                <Input
                  id="min-value"
                  type="number"
                  placeholder="0"
                  className="h-8"
                  value={numValue?.min ?? ''}
                  onChange={e => {
                    const min = e.target.value
                      ? parseFloat(e.target.value)
                      : undefined
                    setLocalValue({
                      type: 'number',
                      value: { ...(numValue || {}), min },
                    })
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label
                  htmlFor="max-value"
                  className="text-xs text-muted-foreground"
                >
                  Max
                </Label>
                <Input
                  id="max-value"
                  type="number"
                  placeholder="999"
                  className="h-8"
                  value={numValue?.max ?? ''}
                  onChange={e => {
                    const max = e.target.value
                      ? parseFloat(e.target.value)
                      : undefined
                    setLocalValue({
                      type: 'number',
                      value: { ...(numValue || {}), max },
                    })
                  }}
                />
              </div>
            </div>
          </div>
        )
      }

      case 'BOOLEAN': {
        const boolValue = localValue?.value as boolean | null | undefined
        return (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button
                variant={boolValue === true ? 'default' : 'outline'}
                size="sm"
                className="h-8 flex-1"
                onClick={() => setLocalValue({ type: 'boolean', value: true })}
              >
                <CheckIcon className="mr-1 h-3 w-3" />
                Yes
              </Button>
              <Button
                variant={boolValue === false ? 'default' : 'outline'}
                size="sm"
                className="h-8 flex-1"
                onClick={() => setLocalValue({ type: 'boolean', value: false })}
              >
                <XIcon className="mr-1 h-3 w-3" />
                No
              </Button>
            </div>
          </div>
        )
      }

      case 'DATE':
      case 'DATETIME': {
        const dateValue = localValue?.value as
          | { min?: string; max?: string }
          | undefined
        return (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label
                htmlFor="from-date"
                className="text-xs text-muted-foreground"
              >
                From
              </Label>
              <DateInputPicker
                valueSource="picker"
                id="from-date"
                triggerVariant="compact"
                triggerClassName="h-8 w-full"
                value={parseCalendarDateValue(dateValue?.min)}
                onChange={date => {
                  const min = date ? toDateOnlyString(date) : undefined
                  setLocalValue({
                    type: 'date',
                    value: { ...(dateValue || {}), min },
                  })
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="to-date"
                className="text-xs text-muted-foreground"
              >
                To
              </Label>
              <DateInputPicker
                valueSource="picker"
                id="to-date"
                triggerVariant="compact"
                triggerClassName="h-8 w-full"
                value={parseCalendarDateValue(dateValue?.max)}
                onChange={date => {
                  const max = date ? toDateOnlyString(date) : undefined
                  setLocalValue({
                    type: 'date',
                    value: { ...(dateValue || {}), max },
                  })
                }}
              />
            </div>
          </div>
        )
      }

      case 'DROPDOWN':
      case 'MULTISELECT': {
        const selectedValues = (localValue?.value as string[]) || []

        const filteredOptions = dropdownOptions.filter(option =>
          option.label.toLowerCase().includes(filterSearchTerm.toLowerCase()),
        )

        return (
          <div className="space-y-2">
            {dropdownOptions.length > 5 && (
              <Input
                aria-label="Search options"
                type="text"
                placeholder="Search options…"
                className="h-8"
                value={filterSearchTerm}
                onChange={e => setFilterSearchTerm(e.target.value)}
              />
            )}
            <ScrollArea className="h-48">
              <div className="space-y-0.5 pr-3">
                {filteredOptions.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No options found
                  </div>
                ) : (
                  filteredOptions.map(option => {
                    const isSelected = selectedValues.includes(option.value)
                    return (
                      <button
                        aria-pressed={isSelected}
                        key={option.value}
                        className={cn(
                          'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent',
                          isSelected && 'bg-accent/50',
                        )}
                        onClick={() => {
                          const newValues = isSelected
                            ? selectedValues.filter(v => v !== option.value)
                            : [...selectedValues, option.value]
                          setLocalValue({
                            type: 'select',
                            value: newValues,
                          })
                        }}
                      >
                        <div
                          className={cn(
                            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                            isSelected && 'border-primary bg-primary',
                          )}
                        >
                          {isSelected && (
                            <CheckIcon className="h-3 w-3 text-primary-foreground" />
                          )}
                        </div>
                        <span className="truncate">{option.label}</span>
                      </button>
                    )
                  })
                )}
              </div>
            </ScrollArea>
            {selectedValues.length > 0 && (
              <div className="border-t pt-1 text-xs text-muted-foreground">
                {selectedValues.length} selected
              </div>
            )}
          </div>
        )
      }

      case 'USER':
      case 'MULTI_USER': {
        const selectedUsers = (localValue?.value as string[]) || []

        const filteredUsers = userOptions.filter(
          user =>
            user.name.toLowerCase().includes(filterSearchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(filterSearchTerm.toLowerCase()),
        )

        return (
          <div className="space-y-2">
            {userOptions.length > 5 && (
              <Input
                aria-label="Search users"
                type="text"
                placeholder="Search users…"
                className="h-8"
                value={filterSearchTerm}
                onChange={e => setFilterSearchTerm(e.target.value)}
              />
            )}
            <ScrollArea className="h-48">
              <div className="space-y-0.5 pr-3">
                {filteredUsers.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No users found
                  </div>
                ) : (
                  filteredUsers.map(user => {
                    const isSelected = selectedUsers.includes(user.id)
                    return (
                      <button
                        aria-pressed={isSelected}
                        key={user.id}
                        className={cn(
                          'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent',
                          isSelected && 'bg-accent/50',
                        )}
                        onClick={() => {
                          const newUsers = isSelected
                            ? selectedUsers.filter(id => id !== user.id)
                            : [...selectedUsers, user.id]
                          setLocalValue({
                            type: 'user',
                            value: newUsers,
                          })
                        }}
                      >
                        <div
                          className={cn(
                            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                            isSelected && 'border-primary bg-primary',
                          )}
                        >
                          {isSelected && (
                            <CheckIcon className="h-3 w-3 text-primary-foreground" />
                          )}
                        </div>
                        <UserAvatar
                          user={{
                            id: user.id,
                            name: user.name,
                            email: user.email,
                          }}
                          size="xs"
                          className="shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">
                            {user.name}
                          </div>
                          {user.email && (
                            <div className="truncate text-xs text-muted-foreground">
                              {user.email}
                            </div>
                          )}
                        </div>
                      </button>
                    )
                  })
                )}
              </div>
            </ScrollArea>
            {selectedUsers.length > 0 && (
              <div className="border-t pt-1 text-xs text-muted-foreground">
                {selectedUsers.length} selected
              </div>
            )}
          </div>
        )
      }

      case 'ADDRESS':
        return (
          <AddressFilterEditor
            value={localValue}
            onChange={setLocalValue}
            onSubmit={() => {
              handleApply()
              handleOpenChange(false)
            }}
          />
        )

      case 'STRING':
      case 'TEXT':
      case 'RICH_TEXT':
      default: {
        const textValue = localValue?.value as string | undefined
        return (
          <div className="space-y-1.5">
            <Input
              aria-label={`Search ${columnLabel.toLowerCase()}`}
              type="text"
              placeholder={`Search ${columnLabel.toLowerCase()}…`}
              className="h-8"
              value={textValue ?? ''}
              onChange={e => {
                setLocalValue({
                  type: 'text',
                  value: e.target.value,
                })
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleApply()
                  handleOpenChange(false)
                }
              }}
              autoFocus
            />
          </div>
        )
      }
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger
        className={triggerClassName}
        title={triggerTitle}
        aria-label={triggerTitle}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        align="start"
        onOpenAutoFocus={e => {
          // Don't auto-focus to prevent immediate keyboard events
          e.preventDefault()
        }}
      >
        {showRename ? (
          // Rename view - edit the column's display label
          <div className="space-y-4 p-4">
            <div className="flex items-center gap-2">
              <button
                aria-label="Cancel rename"
                onClick={() => setShowRename(false)}
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                <XIcon className="h-4 w-4" />
              </button>
              <h4 className="text-sm font-medium">Rename column</h4>
            </div>

            <Input
              aria-label="Column name"
              value={renameValue}
              maxLength={60}
              placeholder={columnLabel}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleRenameSave()
                }
              }}
              autoFocus
            />

            <div className="flex gap-2 pt-2">
              <Button size="sm" onClick={handleRenameSave}>
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowRename(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : !showFilter ? (
          // Menu view - show column actions
          <div className="py-1">
            {canSort && onToggleSort && (
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent"
                onClick={handleSort}
              >
                <ArrowsDownUpIcon className="h-4 w-4" />
                <span>Sort</span>
              </button>
            )}

            {canFilter && onFilterChange && (
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent"
                onClick={toggleFilterView}
              >
                <FunnelIcon className="h-4 w-4" />
                <span>Filter</span>
                {hasActiveFilter && (
                  <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />
                )}
              </button>
            )}

            {canRename && onRename && (
              <>
                {(canSort || (canFilter && onFilterChange)) && (
                  <Separator className="my-1" />
                )}
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent"
                  onClick={openRenameView}
                >
                  <PencilSimpleIcon className="h-4 w-4" />
                  <span>Rename column</span>
                </button>
              </>
            )}

            {canEdit && customFieldDefinitionId && onEditField && (
              <>
                {(canSort ||
                  (canFilter && onFilterChange) ||
                  (canRename && onRename)) && <Separator className="my-1" />}
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent"
                  onClick={handleEdit}
                >
                  <PencilSimpleIcon className="h-4 w-4" />
                  <span>Edit field</span>
                </button>
              </>
            )}

            {canHide && onHideColumn && (
              <>
                {(canSort ||
                  (canFilter && onFilterChange) ||
                  (canEdit && customFieldDefinitionId) ||
                  (canRename && onRename)) && <Separator className="my-1" />}
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent"
                  onClick={handleHide}
                >
                  <EyeSlashIcon className="h-4 w-4" />
                  <span>Hide column</span>
                </button>
              </>
            )}
          </div>
        ) : (
          // Filter view
          <div className="space-y-4 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  aria-label="Close filter"
                  onClick={toggleFilterView}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <XIcon className="h-4 w-4" />
                </button>
                <h4 className="text-sm font-medium">Filter by {columnLabel}</h4>
              </div>
              {hasActiveFilter && (
                <div
                  className="h-2 w-2 rounded-full bg-primary"
                  title="Filter active"
                />
              )}
            </div>

            {renderFilterContent()}

            <div className="flex gap-2 pt-2">
              <Button
                size="sm"
                onClick={() => {
                  handleApply()
                  handleOpenChange(false)
                }}
                className="h-8 flex-1"
              >
                Apply
              </Button>
              {hasActiveFilter && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleClear}
                  className="h-8"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

ColumnMenuPopover.displayName = 'ColumnMenuPopover'
